/* Dawn t3: optional modules share purchase intent; only explicit actions mutate carts. */
(() => {
  if (window.DBCommerce) return;
  const debugParams = new URLSearchParams(location.search);
  const debugEnabled = debugParams.get('theme_debug') === '1' || debugParams.get('upsell_debug') === '1';
  let debugSequence = 0;
  const debugEvent = (event, detail = {}, level = 'info') => {
    if (!debugEnabled) return;
    document.dispatchEvent(new CustomEvent('db-commerce:debug', {
      detail: { event, level, n: ++debugSequence, ...detail },
    }));
  };
  const summarizeProductUpsells = form => {
    const productInfo = form?.closest('product-info');
    const rows = productInfo
      ? [...productInfo.querySelectorAll('product-info-upsell[data-form]')]
      : [];
    const matchingRows = rows.filter(row => row.dataset.form === form.getAttribute('id'));
    return {
      configured: rows.length,
      matched: matchingRows.length,
      selected: matchingRows.filter(row => row.dataset.selected === 'true').length,
      rows: rows.map(row => ({
        key: row.dataset.upsellKey || '',
        id: row.dataset.id || '',
        selected: row.dataset.selected === 'true',
        variantAvailable: row.dataset.variantAvailable === 'true',
        formMatch: row.dataset.form === form.getAttribute('id'),
      })),
    };
  };
  const summarizeAddons = addons => (addons || []).map(addon => ({
    id: String(addon.id || ''),
    scope: addon.scope || '',
    quantity: Number(addon.quantity || 0),
    invalid: Boolean(addon.invalid),
  }));
  const blocked = (reason, message, detail = {}) => {
    debugEvent('prepare.blocked', { reason, ...detail }, 'error');
    throw new Error(message);
  };
  const config = () => { try { return JSON.parse(document.getElementById('DBCommerceConfig')?.textContent || '{}'); } catch { return {}; } };
  const url = path => (config().root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + path;
  const state = new Map(); let tail = Promise.resolve(), pending = 0, revision = 0, needsReview = false;
  const read = async (path, options = {}) => {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(path, { ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      const body = await response.json();
      if (!response.ok || body.status || body.errors) throw new Error(typeof body.description === 'string' ? body.description : typeof body.errors === 'string' ? body.errors : config().error);
      return body;
    } finally { clearTimeout(timer); }
  };
  const fetchResponse = async (path, options) => {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
    debugEvent('request.start', { route: String(path), method: options?.method || 'GET' });
    try { const response = await fetch(path, { ...options, signal: controller.signal });
      const body = await response.text();
      debugEvent('request.result', { route: String(path), status: response.status, bodyShape: body.trim().startsWith('{') ? 'json' : 'other' }, response.ok ? 'info' : 'error');
      return new Response(body, {status:response.status,headers:response.headers});
    } catch (error) {
      debugEvent('request.failure', { route: String(path), reason: String(error?.name || 'network-error') }, 'error');
      throw error;
    } finally { clearTimeout(timer); }
  };
  const recoverSections = async (response, descriptors) => {
    const valid = () => descriptors.every(s => parseSection(response.sections?.[s.section], s.selector));
    if (!valid()) {
      const request = new URL(location.href); request.searchParams.set('sections', [...new Set(descriptors.map(s=>s.section))].join(','));
      response.sections = await read(request.href, { headers: { Accept: 'text/html' } });
      if (!valid()) throw new Error(config().error);
    }
    return response;
  };
  const cart = () => read(url('cart.js'));
  const post = (path, body) => read(url(path), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const keyFor = row => `db-choice-v1:${config().theme}:${config().currency}:${row.dataset.key}:${row.dataset.default}`;
  const choice = row => {
    const key = keyFor(row);
    if (!state.has(key)) {
      let stored; try { stored = JSON.parse(sessionStorage.getItem(key)); } catch {}
      state.set(key, stored && typeof stored.selected === 'boolean' ? stored : { selected: row.dataset.default === 'true', variant: row.querySelector('[data-db-variant]')?.value });
    }
    return state.get(key);
  };
  const save = (row, value) => {
    const key = keyFor(row); state.set(key, value);
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* In-memory intent still works when storage is denied. */ }
  };
  const rows = () => [...document.querySelectorAll('[data-db-addon]')];
  const error = (host, message) => {
    needsReview = true;
    let target = host.querySelector('[data-db-error]');
    if (!target) { target = document.createElement('div'); target.dataset.dbError = ''; target.setAttribute('role', 'alert'); host.append(target); }
    target.hidden = false; target.replaceChildren(document.createTextNode(message || config().error || 'Please check your cart.'));
    const link = document.createElement('a'); link.href = config().cart || url('cart'); link.textContent = config().viewCart || 'View cart'; target.append(' ', link);
  };
  const run = task => {
    revision++;
    pending++;
    document.querySelectorAll('[data-db-wallet], db-addons[data-context=product]').forEach(e=>e.inert=true);
    const promise = tail.then(task);
    tail = promise.catch(() => {});
    return promise.finally(() => {pending--;if(!pending)document.querySelectorAll('[data-db-wallet], db-addons[data-context=product]').forEach(e=>e.inert=false);});
  };
  const whenIdle = async () => {
    let queued;
    do { queued = tail; await queued; } while (queued !== tail);
    if (needsReview) throw new Error(config().error || 'Please check your cart before checkout.');
  };
  const sync = () => {
    rows().forEach(row => {
      const current = choice(row), select = row.querySelector('[data-db-variant]'), input = row.querySelector('[data-db-choice]');
      const option = [...select.options].find(o => o.value === String(current.variant) && !o.disabled);
      if (option) select.value = option.value;
      else select.selectedIndex = -1;
      input.checked = current.selected;
      input.disabled = false;
    });
    document.querySelectorAll('product-form').forEach(syncExpress);
  };
  const selection = (form, quantity) => {
    const seen = new Set(); const selectedRows = rows().filter(r => r.dataset.scope === 'cart' || r.dataset.form === form.getAttribute('id')).flatMap(row => {
      if (seen.has(row.dataset.key)) return []; seen.add(row.dataset.key);
      const c = choice(row), select = row.querySelector('[data-db-variant]');
      const option = [...select.options].find(o => o.value === String(c.variant));
      if (!c.selected) return [];
      return [{ key: row.dataset.key, id: c.variant, invalid: !option || option.disabled, quantity: Number(row.dataset.quantity || 1) * (row.dataset.follow === 'true' ? quantity : 1), nested: row.dataset.nested === 'true', scope: row.dataset.scope }];
    });
    const productInfo = form.closest('product-info');
    const productUpsells = productInfo ? [...productInfo.querySelectorAll('product-info-upsell[data-form]')]
      .filter(row => row.dataset.form === form.getAttribute('id'))
      .flatMap(row => {
        if (row.dataset.selected !== 'true') return [];
        const id = String(row.dataset.id || '');
        const key = String(row.dataset.upsellKey || `${form.getAttribute('id')}-product-upsell-${id}`);
        return [{ key, id, invalid: row.dataset.variantAvailable !== 'true' || !/^[1-9]\d*$/.test(id), quantity: 1, nested: false, scope: 'product', properties: { _theme_upsell_key: key } }];
      }) : [];
    return selectedRows.concat(productUpsells, window.DBSourceCommerce?.selection(form, quantity) || []);
  };
  const capture = (host, submitter) => {
    const data = new FormData(host.form);
    const bundleIds = host.dataset.v4Bundle === 'true' ? data.getAll('items[][id]').filter(Boolean) : [];
    const bundle = host.dataset.v4Bundle === 'true';
    const primaryItems = window.DBPurchaseExtensions?.capture(host, host.form);
    const main = primaryItems?.[0]?.id || (bundle ? String(bundleIds[0] || '') : String(data.get('id') || ''));
    const quantity = primaryItems ? primaryItems.reduce((sum, item) => sum + item.quantity, 0) : (bundle ? 1 : Number(data.get('quantity') || 1));
    const sourceRows = selection(host.form, quantity).filter(row => host.dataset.v6Bundle !== 'true' || host.dataset.includePdpUpsells === 'true' || row.scope === 'cart');
    const addons = sourceRows.concat(bundleIds.slice(1).map((id, index) => ({id, quantity:1, key:`bundle-${host.form.getAttribute('id')}-${index}`, scope:'bundle'})), (primaryItems || []).slice(1).map(item=>({...item,scope:'primary'})), window.DBPurchaseExtensions?.additional(host,host.form) || []);
    const intent = { main, quantity, primaryItems, addons, bundle, checkout: host.dataset.skipCart === 'true' || !!submitter?.hasAttribute('data-db-buy-now') };
    debugEvent('capture.result', {
      formId: host.form.getAttribute('id') || '',
      main: String(main || ''),
      quantity,
      productUpsells: summarizeProductUpsells(host.form),
      sourceRowCount: sourceRows.length,
      addonCount: addons.length,
      addons: summarizeAddons(addons),
      checkout: intent.checkout,
    });
    return intent;
  };
  const prepareDirectCheckout = async (intent, { refreshCart = false } = {}) => {
    if (!intent?.checkout) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    debugEvent('checkout.preflight.start', {
      pendingCartMutation: Boolean(drawer.cartMutationPromise),
      pendingUpsellIntent: Boolean(drawer.upsellIntentMap?.size),
      refreshCart,
    });
    await drawer.flushUpsellIntents?.();
    await drawer.waitForCartMutations?.();
    // The product add request is the operation that creates the primary cart
    // line. A post-add commit must read that new server state before the
    // cart-drawer upsell reconciler decides whether an add-on has a parent.
    if (refreshCart) {
      if (typeof drawer.readCart === 'function') await drawer.readCart();
      else {
        drawer.cartStateStale = true;
        await cart();
      }
    }

    for (const gift of drawer.querySelectorAll('cart-drawer-gift[data-id]')) {
      const result = await gift.update?.();
      if (result?.error) throw result.error;
    }
    await drawer.waitForCartMutations?.();

    let syncResult = null;
    const legacyUpsells = drawer.querySelector('cart-drawer-upsell[data-toggle="true"][data-id]');
    if (legacyUpsells) {
      if (typeof drawer.syncPreselectedUpsells !== 'function') {
        blocked('cart-upsells-not-ready', 'Cart add-ons are not ready.');
      }
      syncResult = await drawer.syncPreselectedUpsells();
      if (syncResult?.error) throw syncResult.error;
      if (!Array.isArray(syncResult?.cart?.items)) {
        blocked('cart-upsells-unverified', 'Cart add-ons could not be verified.');
      }
    }
    await drawer.waitForCartMutations?.();
    debugEvent('checkout.preflight.ready', {
      pendingCartMutation: Boolean(drawer.cartMutationPromise),
      pendingUpsellIntent: Boolean(drawer.upsellIntentMap?.size),
    });
    return syncResult?.cart || drawer.lastKnownCart || null;
  };
  const commitDirectCheckout = async intent => {
    if (!intent?.checkout) return null;

    // This is deliberately a second barrier after /cart/add.js. The old
    // base relies on the primary line being present before cart-drawer
    // add-ons can be attached to it.
    const synced = await prepareDirectCheckout(intent, { refreshCart: true });
    const snapshot = synced?.items ? synced : await cart();
    if (!Array.isArray(snapshot?.items)) {
      blocked('checkout-cart-unverified', 'The cart could not be verified before checkout.');
    }

    const mainId = String(intent.main || '');
    if (mainId && !snapshot.items.some((item) => String(item.variant_id || item.id || '') === mainId)) {
      blocked('checkout-main-unverified', 'The main product could not be verified before checkout.');
    }

    const drawer = document.querySelector('cart-drawer');
    const selectedCartUpsells = drawer && typeof drawer.getToggleUpsells === 'function'
      ? drawer.getToggleUpsells().filter((upsell) => upsell.dataset.selected === 'true')
      : [];
    if (selectedCartUpsells.length && typeof drawer.findCartUpsellItems !== 'function') {
      blocked('cart-upsells-verifier-missing', 'Cart add-ons could not be verified before checkout.');
    }
    selectedCartUpsells.forEach((upsell) => {
      const key = typeof drawer.getUpsellIntentKey === 'function'
        ? drawer.getUpsellIntentKey(upsell)
        : (upsell.dataset.upsellKey || upsell.dataset.handle || '');
      const variantId = String(upsell.dataset.id || '');
      const present = drawer.findCartUpsellItems(snapshot.items, key).some((item) => (
        String(item.variant_id || item.id || '') === variantId
      ));
      if (!present) {
        blocked('cart-addon-unverified', 'A selected cart add-on could not be verified before checkout.', {
          addonKey: key,
          addonId: variantId,
        });
      }
    });
    debugEvent('checkout.commit.ready', {
      cartItemCount: snapshot.items.length,
      selectedCartAddonCount: selectedCartUpsells.length,
      verifiedCartAddonCount: selectedCartUpsells.length,
    });
    return snapshot;
  };
  const prepare = async (data, intent) => {
    debugEvent('prepare.start', { main: String(intent?.main || ''), quantity: intent?.quantity || 0, addonCount: intent?.addons?.length || 0, addons: summarizeAddons(intent?.addons) });
    if (!intent.main) blocked('missing-main', 'Select at least one available product.');
    if (needsReview) blocked('needs-cart-review', config().error || 'Check your cart before another purchase.');
    await prepareDirectCheckout(intent);
    if (intent.primaryItems) { data.set('id',intent.main); data.set('quantity',String(intent.primaryItems[0].quantity)); }
    if (intent.bundle) { data.set('id', intent.main); data.set('quantity', '1'); data.delete('items[][id]'); data.delete('items[][quantity]'); }
    // The accelerated-checkout fields are not Ajax API parameters.
    for (const key of [...data.keys()]) if (key.startsWith('shopify-additional-items[')) data.delete(key);
    if (intent.addons.some(a => a.invalid)) blocked('invalid-addon', 'Choose an available add-on variant or deselect it.', { addons: summarizeAddons(intent.addons) });
    if (!intent.addons.length) {
      debugEvent('prepare.result', { shape: 'native-form-data', itemCount: 1, addonCount: 0 });
      return data;
    }
    let additions = intent.addons;
    if (additions.some(a => a.scope === 'cart')) {
      const snapshot = await cart();
      for (const addon of additions.filter(a => a.scope === 'cart')) {
        const existing = snapshot.items.filter(i => i.properties?._db_addon === addon.key);
        if (existing.some(i => String(i.variant_id) !== String(addon.id))) blocked('cart-addon-variant-mismatch', 'An extra in your cart uses another variant. Update it in the cart before continuing.', { addonId: String(addon.id || '') });
      }
      additions = additions.filter(a => a.scope !== 'cart' || !snapshot.items.some(i => i.properties?._db_addon === a.key));
    }
    if (!additions.length) {
      debugEvent('prepare.result', { shape: 'native-form-data', itemCount: 1, addonCount: 0, filteredCartAddons: true });
      return data;
    }
    if (additions.some(a => a.nested && String(a.id) === intent.main)) blocked('nested-main-variant', 'An add-on cannot be nested under the same variant.', { addons: summarizeAddons(additions) });
    const payload = new FormData();
    for (const [key, value] of data) {
      if (['id', 'quantity', 'selling_plan'].includes(key)) payload.append(`items[0][${key}]`, value);
      else if (key.startsWith('properties[')) payload.append(`items[0][${key.replace('[', '][')}`, value);
      else payload.append(key, value);
    }
    additions.forEach((a, i) => {
      const prefix = `items[${i + 1}]`;
      payload.append(`${prefix}[id]`, a.id); payload.append(`${prefix}[quantity]`, a.quantity);
      if (a.scope !== 'source' && a.scope !== 'bundle' && a.scope !== 'primary') payload.append(`${prefix}[properties][_db_addon]`, a.key);
      if (a.scope === 'primary') {
        for (const [name,value] of data) {
          if (name.startsWith('properties[')) payload.append(`${prefix}[${name.replace('[', '][')}`,value);
          if (name === 'selling_plan' && value) payload.append(`${prefix}[selling_plan]`,value);
        }
      }
      Object.entries(a.properties || {}).forEach(([name, value]) => payload.append(`${prefix}[properties][${name}]`, value));
      if (a.nested) payload.append(`${prefix}[parent_id]`, intent.main);
    });
    debugEvent('prepare.result', { shape: 'multi-item-form-data', itemCount: additions.length + 1, addonCount: additions.length, addons: summarizeAddons(additions) });
    return payload;
  };
  const parseSection = (html, selector) => typeof html === 'string' && html.trim() ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  const validSections = (response, host) => {
    if (!host) return true;
    if (host.tagName === 'CART-NOTIFICATION') host.cartItemKey = response.key;
    return host.getSectionsToRender().every(s => {
      const markup = response.sections?.[s.id], node = parseSection(markup, s.selector || '.shopify-section');
      if (!node) return false;
      return s.selector !== '#CartDrawer' || !!node.querySelector('.cart-item, .drawer__inner-empty');
    });
  };
  const ready = async (response, host, intent) => {
    const main = response.items?.find(i => String(i.variant_id || i.id) === intent.main) || response;
    debugEvent('cart.add.parsed', {
      main: String(intent.main || ''),
      responseItemCount: Array.isArray(response.items) ? response.items.length : undefined,
      responseCartItemCount: Number.isFinite(Number(response.item_count)) ? Number(response.item_count) : undefined,
      responseIds: Array.isArray(response.items) ? response.items.slice(0, 20).map(item => String(item.variant_id || item.id || '')) : [],
    });
    if (!main.id || !main.key) throw new Error(config().error || 'Unable to confirm the added item.');
    const result = { ...response, id: main.id, key: main.key };
    const reconciliation = await window.DBSourceCommerce?.reconcile();
    if (reconciliation?.changed) result.sections = null;
    if (intent.checkout || !host) return result;
    if (!validSections(result, host)) {
      const request = new URL(location.href); request.searchParams.set('sections', host.getSectionsToRender().map(s => s.id).join(','));
      result.sections = await read(request.href, { headers: { Accept: 'text/html' } });
      if (!validSections(result, host)) throw new Error(config().error || 'Added to cart, but the cart display could not be refreshed.');
    }
    return result;
  };
  const refresh = async () => {
    await window.DBSourceCommerce?.reconcile();
    const request = new URL(location.href);
    const ids = new Set([(document.querySelector('cart-drawer')?.dataset.sectionId || 'cart-drawer'),'cart-icon-bubble']);
    document.querySelectorAll('cart-items, cart-drawer-items').forEach(e => e.getSectionsToRender().forEach(s => ids.add(s.section)));
    const footer = document.querySelector('#main-cart-footer'); if (footer?.dataset.id) ids.add(footer.dataset.id);
    request.searchParams.set('sections', [...ids].filter(Boolean).slice(0,5).join(','));
    const sections = await read(request.href, { headers: { Accept: 'text/html' } }); const replacements = [];
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const source = parseSection(sections[drawer.dataset.sectionId || 'cart-drawer'], '#CartDrawer');
      if (!source || !source.querySelector('.cart-item, .drawer__inner-empty')) throw new Error(config().error);
      replacements.push([drawer.querySelector('#CartDrawer'), source.innerHTML]);
      drawer.classList.toggle('is-empty', !!source.querySelector('.drawer__inner-empty') && !source.querySelector('.cart-item'));
    }
    const icon = document.getElementById('cart-icon-bubble'), iconSource = parseSection(sections['cart-icon-bubble'], '.shopify-section');
    if (icon && iconSource) replacements.push([icon,iconSource.innerHTML]);
    document.querySelectorAll('cart-items').forEach(e => e.getSectionsToRender().forEach(s => {
      const source = parseSection(sections[s.section], s.selector), container = document.getElementById(s.id), target = container?.querySelector(s.selector) || container;
      if (!source || !target) throw new Error(config().error);
      replacements.push([target,source.innerHTML]);
    }));
    replacements.forEach(([target, html]) => { if (target) target.innerHTML = html; });
    sync();
    const snapshot = await cart();
    needsReview = false;
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, {source:'db-commerce',cartData:snapshot});
  };
  function syncExpress(host) {
    const form = host.form || host.querySelector('form'); if (!form) return;
    form.querySelectorAll('[data-db-express]').forEach(e => e.remove());
    // The v4 merchant configuration owns additional fields when present.
    // Keep the existing DB add-on path when no legacy product list is configured.
    if (form.querySelector('product-upsell-express') && document.querySelector('cart-drawer [data-express-upsell-config]')) {
      const wallet = form.querySelector('[data-db-wallet]');
      if (wallet) wallet.hidden = !!document.querySelector('v4-cart-drawer-gift, v4-quantity-gifts, v6-quantity-breaks, [data-v6-main-bundle=true]');
      return;
    }
    const selected = selection(form, Number(new FormData(form).get('quantity') || 1));
    const wallet = form.querySelector('[data-db-wallet]');
    const sourceGifts = !!document.querySelector('v4-cart-drawer-gift, v4-quantity-gifts, v6-quantity-breaks, [data-v6-main-bundle=true]');
    const invalid = selected.some(a => a.invalid || (a.nested && a.id === form.querySelector('[name=id]')?.value));
    if (wallet) wallet.hidden = sourceGifts || invalid || (selected.length > 0 && host.dataset.expressAddons !== 'true');
    if (invalid || sourceGifts) return;
    if (host.dataset.expressAddons !== 'true') return;
    const id = form.querySelector('[name=id]')?.value, q = Number(new FormData(form).get('quantity') || 1);
    selection(form, q).forEach((a, i) => {
      const fields = { id: a.id, quantity: a.quantity };
      if (a.nested && a.id !== id) fields.parent_id = id;
      Object.entries(fields).forEach(([name, value]) => { const input = document.createElement('input'); input.type='hidden';input.dataset.dbExpress='';input.name=`shopify-additional-items[${i + 1}][${name}]`;input.value=value;form.append(input); });
    });
  }
  window.DBCommerce = { whenIdle, fetchResponse, recoverSections, run, capture, prepare, ready, commitDirectCheckout, refresh, cart, post, choice, save, sync, error, validSections, url, get pending() { return pending; }, get revision() { return revision; }, navigate: address => {
    if (address !== url('checkout')) { location.assign(address); return; }
    // Match plum's guest checkout: submit the verified cart through Shopify's cart endpoint.
    const checkoutForm = document.createElement('form');
    checkoutForm.method = 'post';
    checkoutForm.action = window.routes?.cart_url || url('cart');
    checkoutForm.hidden = true;
    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'Checkout';
    checkoutForm.appendChild(checkoutInput);
    document.body.appendChild(checkoutForm);
    checkoutForm.submit();
  } };
  document.addEventListener('submit', e => {
    if (e.target.closest('cart-drawer')?.prepareCheckout) return;
    if (e.submitter?.name === 'checkout' && (pending || needsReview)) { e.preventDefault(); error(e.target, config().error); }
  }, true);
  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };
  class Component extends HTMLElement {
    connectedCallback() { this.controller = new AbortController(); this.setup(); }
    on(target, type, cb) { target?.addEventListener(type, cb, {signal:this.controller.signal}); }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.unsubscribe?.(); }
  }
  define('db-addons', class extends Component {
    setup() {
      if (this.dataset.context === 'product') this.inert = pending > 0;
      sync();
      this.on(this, 'change', e => {
        const row = e.target.closest('[data-db-addon]'); if (!row) return;
        const value = {selected:row.querySelector('[data-db-choice]').checked,variant:row.querySelector('[data-db-variant]').value};
        save(row,value);sync();
        if (this.dataset.context === 'product') return;
        const snapshot = {...value,key:row.dataset.key};
        run(async () => {
          const liveRow=rows().find(r=>r.dataset.key===snapshot.key);
          if (!liveRow) return;
          const current=choice(liveRow); if (current.selected!==snapshot.selected || String(current.variant)!==String(snapshot.variant)) return;
          const server=await cart();
          if (!server.items.some(i=>!i.properties?._db_addon)) return; // Empty/ineligible cart never writes a false user choice.
          const managed=server.items.filter(i=>i.properties?._db_addon===snapshot.key);
          const keep=managed.find(i=>String(i.variant_id)===String(snapshot.variant));
          for(const line of managed) if(!snapshot.selected || line!==keep) await post('cart/change.js',{id:line.key,quantity:0});
          if(snapshot.selected && !keep) await post('cart/add.js',{items:[{id:snapshot.variant,quantity:1,properties:{_db_addon:snapshot.key}}]});
          await refresh();
        }).catch(e=>error(this.isConnected?this:document.querySelector('db-addons')||document.body,e.message));
      });
    }
  });
  define('db-discount', class extends Component {
    setup() { this.on(this.querySelector('button'), 'click', () => {
      const code=this.querySelector('input').value.trim();if(!code)return;
      run(async()=>{await post('cart/update.js',{discount:code});await refresh();}).then(()=>{const active=document.querySelector('db-discount');if(active)active.querySelector('[role=status]').textContent=active.dataset.result;}).catch(e=>error(this.isConnected?this:document.querySelector('db-discount')||document.body,e.message));
    }); }
  });
  define('db-delivery', class extends Component {
    setup() { const now=new Date(),min=Math.max(0,Number(this.dataset.min)),max=Math.max(min,Number(this.dataset.max));const date=n=>{const d=new Date(now);d.setDate(d.getDate()+n);return d.toLocaleDateString(this.dataset.locale||undefined,{month:'short',day:'numeric'});};this.querySelector('output').textContent=`${date(min)} – ${date(max)}`; }
  });
  define('db-quantity', class extends Component {
    setup() { this.on(this,'click',e=>{const button=e.target.closest('[data-quantity]');if(!button)return;const form=document.getElementById(this.dataset.form);if(!form)return;let input=[...form.elements].find(e=>e.name==='quantity');if(!input){input=document.createElement('input');input.type='number';input.name='quantity';input.hidden=true;input.min=this.dataset.min||'1';input.step=this.dataset.step||'1';if(this.dataset.max)input.max=this.dataset.max;input.value=input.min;form.append(input);}const before=input.value;input.value=button.dataset.quantity;if(!input.checkValidity()){input.reportValidity();input.value=before;return;}input.dispatchEvent(new Event('change',{bubbles:true}));}); }
  });
  document.addEventListener('change',e=>{
    if(e.target.matches('[name=id],[name=quantity],[data-db-plan-select]')) document.querySelectorAll('product-form').forEach(syncExpress);
  });
  document.addEventListener('upsell:change', () => queueMicrotask(() => document.querySelectorAll('product-form').forEach(syncExpress)));
  document.addEventListener('product-info:loaded',()=>queueMicrotask(sync));
  document.addEventListener('shopify:section:load',()=>queueMicrotask(sync));
  document.addEventListener('DOMContentLoaded',sync,{once:true});
})();
